/**
 * Tier Recommendation Hook for Community Onboarding
 * Handles tier analysis, recommendation logic, and tier details
 */

import { useState, useCallback } from 'react';
import { INTEREST_OPTIONS } from '../../constants/onboarding/progressiveDisclosure';
import { TIER_DETAILS } from '../../constants/onboarding/tierConfiguration';
import type { TierType } from '../../constants/onboarding/tierConfiguration';
import type { UseTierRecommendationReturn } from '../../types/onboarding';

export const useTierRecommendation = (): UseTierRecommendationReturn => {
  const [recommendedTier, setRecommendedTier] = useState<TierType | null>(null);

  const analyzeInterests = useCallback((interests: string[]): TierType => {
    const tierScores = {
      silver: interests.filter(
        (interest) => INTEREST_OPTIONS.find((opt) => opt.id === interest)?.tier === 'silver',
      ).length,
      gold: interests.filter(
        (interest) => INTEREST_OPTIONS.find((opt) => opt.id === interest)?.tier === 'gold',
      ).length,
      custom: interests.filter(
        (interest) => INTEREST_OPTIONS.find((opt) => opt.id === interest)?.tier === 'custom',
      ).length,
    };

    // Advanced scoring algorithm - can be enhanced with weights
    if (tierScores.custom > 0) return 'custom';
    if (tierScores.gold > tierScores.silver) return 'gold';
    if (tierScores.silver > 0) return 'silver';
    return 'silver'; // Default fallback
  }, []);

  const setRecommendation = useCallback((tier: TierType) => {
    setRecommendedTier(tier);
  }, []);

  const tierDetails = recommendedTier ? TIER_DETAILS[recommendedTier] : null;

  return {
    recommendedTier,
    tierDetails,
    analyzeInterests,
    setRecommendation,
  };
};
